import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { loginRequest } from "../../service/authService";
import { Splash } from "../../components/Splash";
import { Mail, Lock, User, Phone, FileText, ArrowLeft, Trash2, Eye, EyeOff, Building2 } from "lucide-react";
import { navigateToDashboard } from "../../utils/navigation";
import { tenantConfig } from "../../config/tenantConfig";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog } from 'primereact/dialog';
import { toast } from "react-toastify";

const InputField = ({ icon: Icon, type = "text", placeholder, value, onChange, isPassword = false }: any) => {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary/70 group-focus-within:text-primary transition-colors">
        <Icon size={20} />
      </div>
      <input
        type={isPassword ? (showPassword ? "text" : "password") : type}
        placeholder={placeholder}
        value={value}
        onChange={(e: any) => onChange(e.target.value)}
        className="w-full bg-inputBg text-text border-none rounded-2xl py-4 pl-12 pr-12 outline-none focus:ring-2 focus:ring-primary/30 transition-all font-medium text-sm placeholder:text-text"
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-text hover:text-text transition-colors"
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      )}
    </div>
  );
};

export const Login: React.FC = () => {
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Views
  const [isLoginView, setIsLoginView] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);

  // Login Controllers
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Register Controllers
  const [regName, setRegName] = useState("");
  const [regTax, setRegTax] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPass, setRegPass] = useState("");

  // Bank Style Account Memory
  const [savedCredentials, setSavedCredentials] = useState<{ email: string; password?: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  // Modal
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

  useEffect(() => {
    loadSavedUsers();
  }, []);

  const loadSavedUsers = () => {
    const rm = localStorage.getItem("remember_me_arya") === "true";
    setRememberMe(rm);

    const saved = localStorage.getItem("saved_creds_arya");
    const rawData: string[] = saved ? JSON.parse(saved) : [];

    const creds = rawData.map(item => {
      const parts = item.split('|');
      return {
        email: parts[0],
        password: parts.length > 1 ? parts[1] : ''
      };
    });

    setSavedCredentials(creds);

    if (creds.length > 0) {
      setSelectedUser(creds[0].email);
      setEmail(creds[0].email);
      if (rm) {
        setPassword(creds[0].password || "");
      }
    }
  };

  const saveUser = (userEmail: string, userPass: string) => {
    const newCreds = savedCredentials.filter(item => item.email !== userEmail);
    newCreds.unshift({ email: userEmail, password: rememberMe ? userPass : "" });

    if (newCreds.length > 5) newCreds.pop();

    setSavedCredentials(newCreds);
    setSelectedUser(userEmail);

    const dataToSave = newCreds.map(item => `${item.email}|${item.password || ""}`);
    localStorage.setItem("saved_creds_arya", JSON.stringify(dataToSave));
    localStorage.setItem("remember_me_arya", String(rememberMe));
  };

  const removeUser = (userEmail: string) => {
    const newCreds = savedCredentials.filter(item => item.email !== userEmail);
    setSavedCredentials(newCreds);

    if (selectedUser === userEmail) {
      if (newCreds.length > 0) {
        setSelectedUser(newCreds[0].email);
        setEmail(newCreds[0].email);
        setPassword(newCreds[0].password || "");
      } else {
        setSelectedUser(null);
        setEmail("");
        setPassword("");
      }
    }

    const dataToSave = newCreds.map(item => `${item.email}|${item.password || ""}`);
    localStorage.setItem("saved_creds_arya", JSON.stringify(dataToSave));
  };

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email || !password) {
      toast.error("Lütfen bilgilerinizi eksiksiz giriniz.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      // API'ye Arya gibi firmalar için özel firmId'yi gönderiyoruz.
      const { user, accessToken, refreshToken } = await loginRequest(email, password, tenantConfig.firmId);
      login(user, accessToken, refreshToken);
      saveUser(email, password);
      toast.success("Giriş başarılı! Yönlendiriliyorsunuz...");
      navigateToDashboard(user, navigate);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regEmail || !regPass || !regName || !regPhone || !regTax) {
      toast.error("Lütfen tüm alanları eksiksiz doldurun.");
      return;
    }

    setLoading(true);
    try {
      // await registerRequest({
      //   email: regEmail,
      //   password: regPass,
      //   name: regName,
      //   phone: regPhone,
      //   taxNumber: regTax
      // });

      setRegEmail("");
      setRegPass("");
      setRegName("");
      setRegPhone("");
      setRegTax("");
      toast.success("Başvurunuz alınmıştır, kurumsal incelemeden sonra tarafınıza bilgi verilecektir.");
      setIsLoginView(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || "Kayıt işlemi başarısız oldu.");
    } finally {
      setLoading(false);
    }
  };

  const getAvatarText = (userEmail?: string | null) => {
    if (!userEmail) return "ARY";
    const namePart = userEmail.split('@')[0];
    const tokens = namePart.split(/[._-]/);

    if (tokens.length >= 3) {
      return (tokens[0][0] + tokens[1][0] + tokens[2][0]).toUpperCase();
    } else if (tokens.length === 2) {
      const combined = tokens.join('');
      return combined.substring(0, Math.min(3, combined.length)).toUpperCase();
    } else {
      return namePart.substring(0, Math.min(3, namePart.length)).toUpperCase();
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center bg-bg overflow-hidden font-sans">

      {/* Gelişmiş Arka Plan (Curtain benzeri organik şekiller) */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-[10%] -left-[10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute top-[20%] -right-[10%] w-[40%] h-[60%] bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-[20%] left-[20%] w-[60%] h-[50%] bg-secondary/10 rounded-full blur-3xl"></div>
      </div>

      {loading && <Splash show={true} />}

      <div className="w-full max-w-md px-6 z-10 py-10 relative">
        <AnimatePresence mode="wait">
          {isLoginView ? (
            <motion.div
              key="login"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="flex flex-col w-full"
            >
              {/* Logo ve Başlık */}
              <div className="flex flex-col items-center mb-8">
                <motion.img
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  src={tenantConfig.logo}
                  alt={tenantConfig.name}
                  className="h-24 object-contain drop-shadow-xl mb-4"
                />
                <motion.h1
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-black text-primary tracking-widest uppercase"
                >
                  {tenantConfig.name}
                </motion.h1>
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-xs text-text font-bold uppercase tracking-widest mt-1"
                >
                  {tenantConfig.slogan}
                </motion.p>
              </div>

              {/* Login Container */}
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="w-full flex flex-col gap-5"
              >
                {selectedUser ? (
                  // Bank Style Account Info
                  <div className="bg-card p-6 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-borderColor flex flex-col items-center relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary to-secondary"></div>

                    <div className="w-20 h-20 rounded-full card2 flex items-center justify-center text-primary font-black text-2xl border-4 border-card shadow-md mb-3 z-10 relative">
                      {getAvatarText(selectedUser)}
                    </div>

                    <h3 className="font-bold text-lg text-text">{selectedUser}</h3>
                    <p className="text-xs text-text font-medium mb-6">Tekrar Hoş Geldiniz</p>

                    <div className="w-full">
                      <InputField
                        icon={Lock}
                        placeholder="Şifreniz"
                        value={password}
                        onChange={setPassword}
                        isPassword
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsAccountModalOpen(true)}
                      className="mt-5 text-primary font-semibold text-sm hover:underline transition-all"
                    >
                      Hesap Değiştir veya Başka Hesapla Gir
                    </button>
                  </div>
                ) : (
                  // Normal Login
                  <div className="bg-card p-6 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-borderColor flex flex-col gap-4">
                    <InputField
                      icon={Mail}
                      type="email"
                      placeholder="E-posta Adresi"
                      value={email}
                      onChange={setEmail}
                    />
                    <InputField
                      icon={Lock}
                      placeholder="Şifre"
                      value={password}
                      onChange={setPassword}
                      isPassword
                    />

                    {savedCredentials.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setIsAccountModalOpen(true)}
                        className="text-primary font-semibold text-sm hover:underline mt-2 self-start"
                      >
                        Kayıtlı Hesaplarımdan Seç
                      </button>
                    )}
                  </div>
                )}

                {/* Beni Hatırla */}
                <div className="flex items-center px-2">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${rememberMe ? 'bg-primary border-primary' : 'border-text/30 group-hover:border-primary'}`}>
                      {rememberMe && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <input type="checkbox" className="hidden" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                    <span className="text-sm font-semibold text-text select-none">Beni Hatırla</span>
                  </label>
                </div>

                <button
                  onClick={() => handleLogin()}
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primaryHover text-white font-bold text-lg py-4 rounded-2xl shadow-md transition-all flex items-center justify-center gap-2"
                >
                  GİRİŞ YAP
                </button>



              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="register"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="flex flex-col w-full"
            >
              <div className="bg-card p-8 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-borderColor flex flex-col relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary to-secondary"></div>

                <h2 className="text-2xl font-black text-primary mb-1 mt-2">Yeni Kayıt Talebi</h2>
                <p className="text-xs text-text font-semibold uppercase tracking-wider mb-8">
                  Cari bilgilerinizi eksiksiz doldurunuz
                </p>

                <form onSubmit={handleRegister} className="flex flex-col gap-4">
                  <InputField icon={Building2} placeholder="Firma Ünvanı / Ad Soyad" value={regName} onChange={setRegName} />
                  <InputField icon={FileText} placeholder="Vergi No / TCKN" value={regTax} onChange={setRegTax} />
                  <InputField icon={Phone} type="tel" placeholder="Telefon" value={regPhone} onChange={setRegPhone} />
                  <InputField icon={Mail} type="email" placeholder="E-posta" value={regEmail} onChange={setRegEmail} />
                  <InputField icon={Lock} placeholder="Şifre Belirleyin" value={regPass} onChange={setRegPass} isPassword />

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full mt-4 bg-primary hover:bg-primaryHover text-white font-bold text-lg py-4 rounded-2xl shadow-md transition-all"
                  >
                    KAYIT TALEBİ GÖNDER
                  </button>
                </form>

                <button
                  type="button"
                  onClick={() => setIsLoginView(true)}
                  className="mt-6 flex items-center justify-center gap-2 text-text font-bold text-sm hover:text-primary transition-colors"
                >
                  <ArrowLeft size={16} />
                  Giriş Ekranına Dön
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Account Selection Modal */}
      <Dialog
        visible={isAccountModalOpen}
        onHide={() => setIsAccountModalOpen(false)}
        position="bottom"
        header="Kayıtlı Hesaplar"
        className="w-full max-w-md mx-4 md:mx-auto rounded-t-3xl md:rounded-3xl overflow-hidden shadow-2xl"
        contentClassName="bg-card p-0"
        headerClassName="bg-card border-b border-borderColor p-5 font-bold text-lg text-primary"
      >
        <div className="flex flex-col py-2">
          {savedCredentials.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 hover:bg-bgAlt border-b border-borderColor transition-colors cursor-pointer group"
              onClick={() => {
                setSelectedUser(item.email);
                setEmail(item.email);
                setPassword(item.password || "");
                setIsAccountModalOpen(false);
              }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  {getAvatarText(item.email)}
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-sm text-text">{item.email}</span>
                  <span className="text-xs text-text font-medium">Kayıtlı Hesap</span>
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeUser(item.email);
                }}
                className="p-2 text-danger/70 opacity-0 group-hover:opacity-100 hover:text-danger hover:bg-danger/10 rounded-full transition-all"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}

          <div
            className="flex items-center gap-4 p-4 hover:bg-bgAlt cursor-pointer transition-colors"
            onClick={() => {
              setSelectedUser(null);
              setEmail("");
              setPassword("");
              setIsAccountModalOpen(false);
            }}
          >
            <div className="w-12 h-12 rounded-full bg-bgAlt flex items-center justify-center text-text font-bold border border-borderColor">
              <User size={20} />
            </div>
            <span className="font-bold text-sm text-text">Yeni Hesapla Giriş Yap</span>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

